/**
 * Google Tasks Task Operation Service - Phase 4 Architecture
 *
 * Encapsulates individual Google Task operations (CRUD) and lookup helpers.
 * Extracted from GoogleTasksSyncService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from "@/services/core/service-registry";
import type {
  GoogleTask,
  CreateGoogleTaskRequest,
  UpdateGoogleTaskRequest,
} from "@/types/google-tasks";
import { GoogleTasksApiClient } from "./google-tasks-api-client.service";
import { GoogleTasksListService } from "./google-tasks-list.service";

const log = logger.createNamespace("GoogleTasksTaskService");

export interface IGoogleTasksTaskService extends IService {
  getTasks(userId: string, listId: string): Promise<ServiceResponse<GoogleTask[]>>;
  createTask(
    userId: string,
    listId: string,
    task: CreateGoogleTaskRequest
  ): Promise<ServiceResponse<GoogleTask>>;
  updateTask(
    userId: string,
    listId: string,
    taskId: string,
    updates: UpdateGoogleTaskRequest
  ): Promise<ServiceResponse<GoogleTask>>;
  completeTask(userId: string, listId: string, taskId: string): Promise<ServiceResponse<void>>;
  deleteTask(userId: string, listId: string, taskId: string): Promise<ServiceResponse<void>>;
  getGoogleTaskById(listId: string, taskId: string): Promise<GoogleTask>;
  getTasksForListOwner(listId: string, taskId: string): Promise<GoogleTask>;
}

export class GoogleTasksTaskService implements IGoogleTasksTaskService {
  public readonly name = "GoogleTasksTaskService";
  private readonly supabase = typedSupabase;
  private apiClient: GoogleTasksApiClient;
  private listService: GoogleTasksListService;

  constructor(apiClient: GoogleTasksApiClient, listService: GoogleTasksListService) {
    this.apiClient = apiClient;
    this.listService = listService;
  }

  async initialize(): Promise<void> {
    log.info("Google Tasks Task Service initialized");
  }

  async dispose(): Promise<void> {
    log.info("Google Tasks Task Service disposed");
  }

  async getTasks(userId: string, listId: string): Promise<ServiceResponse<GoogleTask[]>> {
    log.debug("Fetching tasks", { userId, listId });

    const queryParams = new URLSearchParams({
      showCompleted: "true",
      showHidden: "true",
    });

    const result = await this.apiClient.request<{ items?: GoogleTask[] }>(
      userId,
      `/lists/${listId}/tasks?${queryParams.toString()}`
    );

    if (!result.success) {
      log.error("Failed to fetch tasks", { error: result.error });
      return createErrorResponse(result.error ?? "Failed to fetch tasks");
    }

    log.success("Fetched tasks", { count: result.data?.items?.length ?? 0 });
    return createSuccessResponse(result.data?.items ?? []);
  }

  async createTask(
    userId: string,
    listId: string,
    task: CreateGoogleTaskRequest
  ): Promise<ServiceResponse<GoogleTask>> {
    log.info("Creating Google Task", { userId, listId, title: task.title });

    const result = await this.apiClient.request<GoogleTask>(
      userId,
      `/lists/${listId}/tasks`,
      {
        method: "POST",
        body: JSON.stringify(task),
      }
    );

    if (!result.success || !result.data) {
      log.error("Failed to create task", { error: result.error });
      return createErrorResponse(result.error ?? "Failed to create task");
    }

    const createdTask = result.data;

    await this.apiClient.logGoogleTasksActivity(
      userId,
      listId,
      createdTask.id,
      "created",
      "gtasks.task_created",
      {
        task_title: createdTask.title,
        task_notes: createdTask.notes,
        due_date: createdTask.due,
      }
    );

    return createSuccessResponse(createdTask);
  }

  async updateTask(
    userId: string,
    listId: string,
    taskId: string,
    updates: UpdateGoogleTaskRequest
  ): Promise<ServiceResponse<GoogleTask>> {
    log.info("Updating Google Task", { userId, listId, taskId });

    const result = await this.apiClient.request<GoogleTask>(
      userId,
      `/lists/${listId}/tasks/${taskId}`,
      {
        method: "PATCH",
        body: JSON.stringify(updates),
      }
    );

    if (!result.success || !result.data) {
      log.error("Failed to update task", { error: result.error });
      return createErrorResponse(result.error ?? "Failed to update task");
    }

    const updatedTask = result.data;

    await this.apiClient.logGoogleTasksActivity(
      userId,
      listId,
      taskId,
      "updated",
      "gtasks.task_updated",
      {
        task_title: updatedTask.title,
        task_notes: updatedTask.notes,
        due_date: updatedTask.due,
        status: updatedTask.status,
      }
    );

    return createSuccessResponse(updatedTask);
  }

  async completeTask(
    userId: string,
    listId: string,
    taskId: string
  ): Promise<ServiceResponse<void>> {
    log.info("Completing Google Task", { userId, listId, taskId });

    const updateResult = await this.updateTask(userId, listId, taskId, {
      status: "completed",
    });

    if (!updateResult.success) {
      return createErrorResponse(updateResult.error ?? "Failed to complete task");
    }

    await this.apiClient.logGoogleTasksActivity(
      userId,
      listId,
      taskId,
      "completed",
      "gtasks.task_completed",
      {
        completed_date: new Date().toISOString(),
      }
    );

    return createSuccessResponse(undefined);
  }

  async deleteTask(
    userId: string,
    listId: string,
    taskId: string
  ): Promise<ServiceResponse<void>> {
    log.info("Deleting Google Task", { userId, listId, taskId });

    const result = await this.apiClient.request<void>(
      userId,
      `/lists/${listId}/tasks/${taskId}`,
      {
        method: "DELETE",
      }
    );

    if (!result.success) {
      log.error("Failed to delete task", { error: result.error });
      return createErrorResponse(result.error ?? "Failed to delete task");
    }

    await this.apiClient.logGoogleTasksActivity(
      userId,
      listId,
      taskId,
      "deleted",
      "gtasks.task_deleted",
      {
        task_id: taskId,
        list_id: listId,
      }
    );

    return createSuccessResponse(undefined);
  }

  async getGoogleTaskById(listId: string, taskId: string): Promise<GoogleTask> {
    return this.getTasksForListOwner(listId, taskId);
  }

  async getTasksForListOwner(listId: string, taskId: string): Promise<GoogleTask> {
    const { data: listRecord, error } = await this.supabase
      .from("google_task_lists")
      .select("user_id")
      .eq("google_task_list_id", listId)
      .single();

    if (error || !listRecord?.user_id) {
      throw new Error("Google Task list owner not found");
    }

    const taskResult = await this.getTasks(listRecord.user_id, listId);
    if (!taskResult.success || !taskResult.data) {
      throw new Error(
        typeof taskResult.error === "string" ? taskResult.error : "Failed to fetch Google Tasks"
      );
    }

    const task = taskResult.data.find((item) => item.id === taskId);
    if (!task) {
      throw new Error("Google Task not found");
    }

    return task;
  }
}
